import {
  AmenityEnum,
  AttachmentStatus,
  AttachmentType,
  CourtStatus,
  EntityStatus,
  EventStatus,
  FacilityStatus,
  ParticipantSide,
  PaymentMethod,
  PaymentStatus,
  PaymentType,
  SlotCategoryEnum,
  TournamentGender,
  TournamentType,
  UserRole,
} from '../enums/enums';
import { Dayjs } from 'dayjs';

export interface Sport {
  sportId: number;
  sportName: string;
  sportDescription: string;
  createdOn: string;
  isTeamSport?: boolean;
  sportMinPlayersPerTeam?: number;
  sportMaxPlayersPerTeam?: number;
  attachmentUrlDto: AttachmentInfo;
  isActive: boolean;
}
export interface SportSearchParams {
  searchQuery?: string;
  pageNumber?: number;
  pageSize?: number;
}

export type LoginFormValues = { mobile: string } | { otp: string };

export type TokenResponse = {
  success: boolean;
  userId: string;
  email: string;
  name: string;
  token: string;
  error: string;
};

export type ApiResponse<T = undefined> = {
  value?: T;
  isSuccess: boolean;
  error: string;
  isFailure: boolean;
};

export type GridResponse<T> = {
  rows: T;
  totalCount: number;
};

export type FacilityCreateType = {
  facilityName: string;
  facilityDescription: string;
  facilityLocation: string;
  facilityAddress: string;
  state: string;
  country: string;
  city: string;
  zipCode: string;
  facilityLatitude: number;
  facilityLongitude: number;
  facilityContactName: string;
  facilityContactPhone: string;
  facilityContactEmail: string;
  facilityOwnerId: number;
  openingTime: string;
  closingTime: string;
  sportsIds: number[];
  facilityAmenityIds: AmenityEnum[];
  facilityStatus: FacilityStatus;
};

export type FacilityDetailWithDayjsTimes = Omit<
  FacilityDetailType,
  'openingTime' | 'closingTime'
> & {
  openingTime?: Dayjs;
  closingTime?: Dayjs;
};

export interface FacilityUpdateType extends FacilityCreateType {
  facilityId: number;
}

export interface FacilityType {
  facilityId: number;
  facilityName: string;
  facilityLocation: string;
  facilityAddress: string;
  state: string;
  country: string;
  city: string;
  zipCode: string;
  facilityLatitude: number;
  facilityLongitude: number;
  facilityContactName: string;
  facilityContactPhone: string;
  facilityContactEmail: string;
  facilityStatus: FacilityStatus;
  facilityStatusName: string;
  entityStatus: EntityStatus;
  entityStatusName: string;
  sportsNames: string[];
  distanceFromUser?: number;
  primaryImageUrl?: string;
}

export interface FacilityDetailType {
  facilityId: number;
  facilityName: string;
  facilityDescription: string;
  facilityLocation: string;
  facilityAddress: string;
  state: string;
  country: string;
  city: string;
  zipCode: string;
  facilityLatitude: number;
  facilityLongitude: number;
  facilityContactName: string;
  facilityContactPhone: string;
  facilityContactEmail: string;
  facilityStatus: FacilityStatus;
  facilityStatusName: string;
  entityStatus: EntityStatus;
  entityStatusName: string;
  sports: SportLiteType[];
  openingTime: string;
  closingTime: string;
  facilityOwnerId: number;
  facilityAmenities: AmenityEnum[];
  facilitySecondaryContactPhone: string;
  faciltySecondaryContactEmail: string;
  activeBookingsCount: number;
  primaryImageUrl: string;
  attachments: AttachmentData[];
}
export interface FacilitySearchParams {
  location?: string;
  sportIds?: number[];
  minPrice?: number;
  maxPrice?: number;
  sortDirection?: 0 | 1;
  sortBy?: string;
  page?: number;
  size?: number;
  searchQuery?: string;
}

export interface SportDto {
  sportId: number;
  sportName: string;
  defaultPrice: number;
}

export interface FacilitySportDto {
  facilitySportId: number;
  sportId: number;
  sportName: string;
  regularHourlyPrice: number;
  weekendHourlyPrice: number;
  totalCourts: number;
  availableCourts: number;
  courts: CourtDto[];
}

export type CourtDto = {
  courtId: number;
  facilitySportId: number;
  courtName: string;
  courtDescription: string;
  courtStatus: number;
  courtStatusName: string;
  defaultPrice: number;
  sportName: string;
  facilityName: string;
  totalSlots: number;
  availableSlots: number;
  slots: SlotDto[];
  regularHourlyPrice?: number;
  weekendHourlyPrice?: number;
};

export type SlotDto = {
  slotId: number;
  courtId: number;
  startTime: string;
  endTime: string;
  price: number;
  isAvailable: boolean;
  sportName: string;
  courtName: string;
  facilityName: string;
  slotStatus: number;
};

export type SlotSessions = {
  title: string;
  slots: SlotDto[];
  icon: React.ReactNode;
}[];

export type SportLiteType = {
  sportId: number;
  sportName: string;
  sportImageUrl: string;
  isTeamSport: boolean;
};

export type CourtLiteType = {
  courtId: number;
  courtName: string;
};

export interface CourtFormValue {
  courtId?: number;
  courtName: string;
  isNew?: boolean;
}

export interface CreateSportType {
  facilityId: number;
  sportId: number;
  courts: string[];
  regularHourlyPrice: number;
  weekendHourlyPrice: number;
}

export interface CreateSportFormType extends Omit<CreateSportType, 'courts'> {
  courts: CourtFormValue[];
}

export interface EditSportType extends CreateSportType {
  existingCourts: CourtLiteType[];
}

export interface EditSportFormType extends Omit<EditSportType, 'courts'> {
  courts: CourtFormValue[];
}

export interface UserSearchParams {
  userRole?: UserRole;
  PageNumber?: number;
  PageSize?: number;
  SearchQuery?: string;
  EntityStatus?: number;
}

export interface DateSearchParams {
  startDate?: string;
  endDate?: string;
}

export interface BookingsListParams {
  userId?: number;
  sportId?: number;
  facilityId?: number;
  status?: number;
  fromDate?: string;
  toDate?: string;
  showBookingSlots?: boolean;
  PageNumber?: number;
  PageSize?: number;
  SearchQuery?: string;
}

export interface UserType {
  userId: number;
  fullName: string | null;
  email: string | null;
  phoneNumber: string | null;
  userRole: UserRole;
  entityStatus: EntityStatus;
  profilePictureUrl?: string;
  bio?: string;
  createdOn?: string;
}

export interface UserProfileType {
  userId: number;
  fullName: string | null;
  email: string | null;
  phoneNumber: string | null;
  role: UserRole;
  profilePictureUrl?: string;
  bio?: string;
  createdOn?: string;
  entityStatus: EntityStatus;
}

export interface UserCreateType {
  fullName: string;
  email: string;
  phoneNumber: string;
  role: number;
  bio?: string;
  profilePictureUrl?: any;
}

export interface CreateUserByAdminType extends UserCreateType {
  entityStatus: number;
}

export interface BookingData {
  id: string;
  sport: string;
  court: string;
  dateTime: string;
  bookedBy: string;
  status: 'completed' | 'upcoming' | 'cancelled';
}

export type AmenityType = {
  label: string;
  value: AmenityEnum;
};

export interface CreateSlotType {
  courtId?: number;
  slotTimings: SlotTimings[];
  repeatForDays?: number;
  timeZoneId?: string;
}

export type SlotTimings = {
  startTime: Date | string;
  endTime: Date | string;
};

export type ReserveSlotsType = {
  slotIds: number[];
  note?: string;
  expiresAt?: string;
};

export type UnreserveSlotsType = {
  slotIds: number[];
};

export type UpdateSlotType = {
  courtId: number;
  startTime: Date;
  endTime: Date;
  slotId: number;
};

export interface BookingsType {
  bookingId: number;
  bookingReference: string;
  userId: number;
  bookingDate: Date;
  totalAmount: number;
  status: number;
  statusName: string;
  userName: string;
  phoneNumber: string;
  email: string;
  facilityName: string;
  sportName: string;
  courtName: string;
  paymentDeadline: Date;
  cancellationReason: string;
  bookingSlots: any;
}

export type CreateMultipleCourtType = {
  facilitySportId: number;
  courtNames: CourtPricingDetail[];
};

export type CourtPricingDetail = {
  courtName: string;
  regularHourlyPrice?: number;
  weekendHourlyPrice?: number;
};

export type UpdateCourtType = {
  courtId: number;
  courtName: string;
  courtDescription: string;
  courtStatus: CourtStatus;
  regularHourlyPrice?: number;
  weekendHourlyPrice?: number;
};

export interface UploadFileResponse {
  AttachmentId: number;
  AttachmentUrl: string;
  OriginalFileName: string;
  FileSizeBytes: number;
  AttachmentType: AttachmentType;
  Message: string;
}

export interface BulkUploadResponse {
  successfulUploads: UploadFileResponse[];
  failedUploads: FailedUploadResponse[];
  totalFiles: number;
  successCount: number;
  failureCount: number;
}

export interface FailedUploadResponse {
  fileName: string;
  errorMessage: string;
}

export interface AttachmentData {
  attachmentId: number;
  originalFileName: string;
  contentType: string;
  fileSizeBytes: number;
  fileSizeFormatted: string;
  attachmentType: AttachmentType;
  attachmentTypeName: string;
  attachmentDescription: string;
  attachmentUrl: string;
  attachmentStatus: AttachmentStatus;
  attachmentStatusName: string;
  createdDate: Date;
  isPrimary: boolean;
  displayOrder: number;
}

export interface UpdateFacilityStatusType {
  facilityId: number;
  facilityStatus: number;
}
export interface SportsCreationType {
  sportName: string;
  description: string;
  sportCode: string;
  isTeamSport: boolean;
}
export interface FacilitySearchParams {
  PageNumber?: number;
  PageSize?: number;
  SearchQuery?: string;
  FacilityStatus?: number;
}

export interface AttachmentInfo {
  fileName: string;
  displayOrder: number;
  isPrimary: boolean;
  attachmentId: number;
  attachmentUrl: string;
}

export type EventSearchParams = {
  searchTerm?: string;
  sportIds?: number[];
  status?: EventStatus;
  startDateFrom?: string;
  startDateTo?: string;
  createdBy?: number;
  location?: string;
  pageNumber?: number;
  pageSize?: number;
  searchQuery?: string;
};

export type EventType = {
  eventId: number;
  eventName: string;
  eventDescription: string;
  tournamentCount: number;
  sportNames: string[];
  registrationCount: number;
  eventStartDate: string;
  eventEndDate: string;
  registrationStartDate: string;
  registrationEndDate: string;
  eventStatus: EventStatus;
  eventStatusName: string;
  primaryImageUrl: string;
  registrationFee: number;
  attachmentId: number;
  eventLocation?: string;
  eventLocationLatitude?: number | null;
  eventLocationLongitude?: number | null;
  eventOrganizerUserId?: number | null;
};

export interface EventCreateRequest {
  eventBasicDetails: EventBasicDetails;
  eventSports: EventSport[];
}

export interface EventBasicDetails {
  eventName: string | null;
  eventDescription: string | null;
  eventStartDate: string;
  eventEndDate: string;
  registrationStartDate: string | null;
  registrationEndDate: string | null;
  registrationFee: number | null;
  eventLocation: string;
  eventLocationLatitude: number | null | undefined;
  eventLocationLongitude: number | null | undefined;
  eventOrganizerUserId?: number | null;
}

export interface EventSport {
  sportId: number;
  eventGenders: {
    gender: number; // consider enum/union if fixed
    customCategories: {
      customCategoryName: string;
      tournamentTypes: number[];
    }[];
  }[];
}

export interface EventUpdateRequest {
  eventName: string;
  eventDescription: string;
  eventStartDate: Dayjs | null;
  eventEndDate: Dayjs | null;
  registrationStartDate: Dayjs | null;
  registrationEndDate: Dayjs | null;
  eventStatus: EventStatus | undefined;
  registrationFee?: number;
  eventLocation?: string;
  eventLocationLatitude?: number | null;
  eventLocationLongitude?: number | null;
  eventId: number;
  eventOrganizerUserId?: number | null;
}

export interface TournamentSummary {
  tournamentId: number;
  tournamentName: string | null;
  tournamentFormat: number | null;
  tournamentFormatName: string | null;
  tournamentStatus: number;
  tournamentStatusName: string;
  tournamentType: number;
  tournamentTypeName: string;
  tournamentGender: number;
  tournamentGenderName: string;
  customTournamentCategory: string | null;
  isDoubles: boolean;
  isTeamTournament: boolean;
  startDate: string;
  endDate: string;
  registrationStartDate: string | null;
  registrationEndDate: string | null;
  entryFee: number;
  participantCount: number;
  sportAttachmentUrl: string | null;
  sportId: number;
  sportName: string | null;
  bracketGenerated: boolean;
}

export interface EventDetails {
  eventId: number;
  eventName: string;
  eventDescription: string | null;
  attachmentUrl: string | null;
  eventStartDate: string;
  eventEndDate: string;
  registrationStartDate: string | null;
  registrationEndDate: string | null;
  eventLocation: string | null;
  eventLocationLatitude: number | null;
  eventLocationLongitude: number | null;
  eventStatus: number;
  eventStatusName: string;
  registrationFee: number;
  totalParticipants: number;
  totalRevenue: number;
  totalTournaments: number;
  isAlreadyRegistered: boolean;
  tournamentSummaries: TournamentSummary[];
}

export interface TournamentCreateRequest {
  eventId: number;
  sportId: number;
  facilityId?: number;
  tournamentName: string;
  tournamentDescription: string;
  tournamentLocation?: string;
  tournamentLocationLatitude?: number;
  tournamentLocationLongitude?: number;
  tournamentFormat?: number;
  customTournamentCategory: string;
  gender: number;
  startDate: string;
  endDate: string;
  tournamentType: TournamentType;
  entryFee: number;
  rules: string;
  prizeStructure: string;
  seedingType?: number;
}

export interface RecentRegistration {
  tournamentParticipantId: number;
  participantId: number;
  participantName: string;
  participantEmail: string;
  participantPhone: string;
  registrationDate: string;
  registrationFee: number;
  seedNumber: number;
  ranking: number | null;
  status: number;
  statusName: string;
  teamId: number | null;
}

export type GenderKey = 'female' | 'male' | 'mixed';

export const genderKeys: GenderKey[] = ['female', 'male', 'mixed'];

export const defaultCategories = ['Junior', 'Senior', 'Adult'];

export type DateFieldKey =
  | 'eventStartDate'
  | 'eventEndDate'
  | 'registrationStartDate'
  | 'registrationEndDate';

export type EventFormState = {
  eventName: string | null;
  description: string;
  eventImage?: any | null;
  eventStartDate: Dayjs | null;
  eventEndDate: Dayjs | null;
  registrationStartDate: Dayjs | null;
  registrationEndDate: Dayjs | null;
  eventAddress: string;
  registrationFee: number | null;
  selectedSports: number[];
  categoriesBySport: Record<number, string[]>;
  tournaments: TournamentSelection[];
  eventLat?: number | null;
  eventLng?: number | null;
  eventOrganizerUserId?: number | null;
};

export type TournamentSelection = {
  [sportId: number]: {
    male?: { [categoryId: string]: number[] };
    female?: { [categoryId: string]: number[] };
    mixed?: { [categoryId: string]: number[] };
  };
};

export interface ImageItem {
  uri: string;
  file: File;
}

export interface TournamentsByGender {
  [category: string]: number[];
}

export interface TournamentsBySport {
  female: TournamentsByGender;
  male: TournamentsByGender;
  mixed: TournamentsByGender;
}

export type FlattenedTournament = {
  sportId: number;
  gender: string;
  categoryName: string;
  typeName: string;
};

export interface CustomCategoryDto {
  customCategoryName: string;
  tournamentTypes: TournamentType[];
}

export interface CreateEventSportsDto {
  sportId: number;
  eventGenders: EventGendersDto[];
}

export interface EventGendersDto {
  gender: TournamentGender;
  customCategories: CustomCategoryDto[];
}

export interface Participant {
  participantId: number;
  participantName: string;
  participantEmail: string;
  participantPhone: string;
}

export interface PairedParticipant {
  teamId: number;
  participantName: string;
  partnerName: string;
}

export interface TournamentDetailsData {
  totalRegisteredCount: number;
  totalPairedCount: number;
  totalUnpairedCount: number;
  unpairedParticipants: Participant[];
  pairedParticipants: PairedParticipant[];
}

export interface ParticipantDto {
  participantId: number;
  participantName: string;
  partnerName?: string;
}

export interface MatchDto {
  tournamentMatchId: number;
  statusName: string;
  scheduledStartTime: string;
  matchLocation: string;
  participant1ScoreData: string;
  participant2ScoreData: string;
  facilityId: number;
}

export interface BracketMatchupDto {
  matchId: number;
  participant1?: ParticipantDto;
  participant2?: ParticipantDto;
  winner?: ParticipantDto;
  match?: MatchDto;
}

export interface RoundDto {
  round: number;
  roundName: string;
  matchups: BracketMatchupDto[];
}

export interface BracketDetailsData {
  totalMatchups: number;
  totalCompletedMatchups: number;
  rounds: RoundDto[];
  bracketGenerated?: boolean;
  /** True when players registered after this bracket was generated; bracket is read-only until regenerated. */
  bracketIsStale?: boolean;
  /** Count of confirmed participants not yet represented in the current bracket. */
  newRegistrationsSinceGeneration?: number;
  /** Doubles/mixed-doubles: confirmed players who couldn't be paired into the bracket. Informational. */
  unpairedLeftoverCount?: number;
  /** True when the registration window is still open; scoring is blocked until it closes. */
  registrationWindowOpen?: boolean;
}

export interface UpdateTournamentPayload {
  tournamentId: number;
  entryFee: number;
  startDate: string;
  endDate: string;
  registrationStartDate: string | null;
  registrationEndDate: string | null;
  tournamentName: string | null;
  tournamentType: TournamentType;
  customTournamentCategory: string | null;
  gender: TournamentGender;
  rules: string;
  prizeStructure: string;
}

export interface TeamMember {
  teamMemberId: number;
  memberId: number;
  memberName: string;
  email: string | null;
  phoneNumber: string | null;
  profilePictureUrl: string | null;
  isCaptain: boolean;
  createdOn: string;
}

export interface Team {
  teamId: number;
  teamName: string;
  teamDescription: string;
  teamLogo: string | null;
  teamCaptainId: number;
  captainName: string | null;
  memberCount: number;
  members: TeamMember[];
  isActive: boolean;
  createdOn: string;
}

export interface ParticipantScoreDto {
  participantId: number | null;
  participantName: string;
  partnerId: number | null;
  partnerName: string | null;
  isPaired: boolean;
  totalScore: number | null;
  scoreData: string;
  scoreDataJson: ScoreDataJson;
}

export interface TennisSet {
  games1: number;
  games2: number;
  tiebreakScore1: number | null;
  tiebreakScore2: number | null;
}

export interface TennisScore {
  sets: TennisSet[];
  currentSet: TennisSet;
}

export interface SimpleSetScore {
  sets: number[];
  currentSetScore: number;
}

export interface GameScore {
  games: number[];
  currentGameScore: number;
}

export type SetScore = {
  id: number;
  value: string;
};

export interface TournamentMatchParticipant {
  participantId: number;
  participantName: string;
  partnerId: number | null;
  partnerName: string | null;
  isPaired: boolean;
  totalScore: number;
  scoreData: string;
  scoreDataJson: ScoreDataJson;
}

export interface ScoreDataJson {
  sets?: number[];
  games?: number[];
  currentSetScore?: number;
  currentGameScore?: number;
}

export interface TournamentMatchScoreValue {
  tournamentMatchId: number;
  tournamentId: number;
  tournamentName: string;
  sportId: number;
  sportName: string;
  round: number;
  matchNumber: number;
  matchStatus: 'Pending' | 'Completed' | 'Ongoing' | 'Cancelled';
  participant1: TournamentMatchParticipant;
  participant2: TournamentMatchParticipant;
  winnerId: number | null;
  winnerName: string | null;
  actualStartTime: string | null;
  actualEndTime: string | null;
  isByeMatch: boolean;
}

export interface DeclareTournamentMatchWinnerPayload {
  tournamentMatchId: number;
  autoAdvanceWinner: boolean;
  isMatchByeRound: boolean;
  winnerId: number;
  participant1Score: number;
  participant1ScoreData?: string | null;
  participant2Score: number;
  participant2ScoreData?: string | null;
  matchNotes: string;
  refereeNotes: string;
}

export interface UpdateScorePayloadType {
  tournamentMatchId: number;
  scoringParticipant: ParticipantSide;
  scoreDataJson: string;
}
export type SetRow = {
  id: number;
  p1: string;
  p2: string;
};

export interface TournamentMatchUpdatePayload {
  tournamentMatchId: number;
  scheduledStartTime: string;
  tournamentMatchLocation?: string;
  tournamentMatchLocationLatitude?: number;
  tournamentMatchLocationLongitude?: number;
  facilityId?: number;
  matchNotes?: string;
  refereeNotes?: string;
}

export interface MatchNavigationState {
  returnTo?: string;
  selectedRound?: number;
  currentPage?: number;
}

interface PaymentLineItemInfo {
  paymentLineItemId: number;
  amount: number;
  paymentLineItemStatus: number;
  paymentLineItemStatusName: string;
  paymentLineItemType: number;
  paymentLineItemTypeName: string;
  isCancellable: boolean;
  cancelledDate: string | null;
}

export interface BookingSlotType {
  bookingSlotId: number;
  slotId: number;
  slotPrice: number;
  startTime: string;
  endTime: string;
  courtName: string;
  facilityId: number;
  facilityName: string;
  facilityAddress: string;
  facilityImageUrl: string;
  sportName: string;
  isCancellable: boolean;
  cancelledDate: string | null;
  paymentLineItemInfo: PaymentLineItemInfo;
}

interface PaymentInfo {
  amount: number;
  paymentDate: string;
  paymentLineItems: PaymentLineItemInfo[];
  paymentMethod: number;
  paymentStatus: number;
  paymentStatusName: string;
  paymentUrl: string | null;
  transactionId: string;
}

export interface BookingDetailsType {
  bookingId: number;
  bookingReference: string;
  bookingDate: string;
  bookingSlots: BookingSlotType[];
  paymentDeadline: string;
  paymentInfo: PaymentInfo;
  status: number;
  statusName: string;
  totalAmount: number;
  userId: number;
  userName: string;
}

export interface CategoryGroup {
  category: SlotCategoryEnum;
  slots: BookingSlotType[];
}

export interface DateGroup {
  date: string;
  categories: CategoryGroup[];
}

// --- Transaction Types ---
export interface TransactionSearchParams {
  PaymentType?: PaymentType;
  PaymentStatus?: PaymentStatus;
  PaymentMethod?: PaymentMethod;
  FromDate?: string;
  ToDate?: string;
  UserId?: number;
  FacilityId?: number;
  EventId?: number;
  PageNumber?: number;
  PageSize?: number;
  SearchQuery?: string;
}

export interface TransactionGridResponse {
  totalCount: number;
  rows: TransactionDto[];
  totalAmount: number;
  totalRefundedAmount: number;
  totalNetAmount: number;
  totalPendingAmount: number;
}

export interface EventLiteDto {
  eventId: number;
  eventName: string;
}

export interface FacilityLiteDto {
  facilityId: number;
  facilityName: string;
}

export interface TransactionLineItemDto {
  paymentLineItemId: number;
  paymentLineItemType: number;
  paymentLineItemTypeName: string;
  referenceId: number;
  referenceName: string;
  amount: number;
  refundedAmount: number;
  paymentLineItemStatus: number;
  paymentLineItemStatusName: string;
  description: string;
}

export interface TransactionDto {
  paymentId: number;
  transactionId: string;
  providerOrderId: string;
  paymentType: number;
  paymentTypeName: string;
  amount: number;
  refundedAmount: number;
  paymentStatus: number;
  paymentStatusName: string;
  paymentMethod: number;
  paymentMethodName: string;
  paymentDate: string;
  createdOn: string;
  userId: number;
  userName: string;
  userPhone: string;
  relatedEntityName: string;
  relatedEntityId: number;
  lineItems: TransactionLineItemDto[];
}
